import { useCallback, useEffect, useRef, useState } from 'react';

import { getJWTToken } from '../../utils/auth';
import type { PeContent, PeDrilldown, PeReport, ScoreUnit } from './types';
import { authHeaders } from '../../core/impersonation';
import { DrilldownNotFound, fetchDrilldown } from './drilldownApi';

export type PeDataSource = () => Promise<{
  report: PeReport | null;
  content: PeContent | null;
  stale?: boolean;
  generatedAt?: string | null;
}>;

const apiUrl = import.meta.env.REACT_APP_BAMBOO_API_URL ?? '';

type PeArtifactResponse = {
  version: number;
  unitMode: ScoreUnit;
  generatedAt: string;
  report: PeReport | null;
  content: (Omit<PeContent, 'unit_mode'> & { unit_mode?: ScoreUnit }) | null;
  stale?: boolean;
};

export type PeArtifact = {
  report: PeReport | null;
  content: PeContent | null;
  stale: boolean;
  generatedAt: string | null;
  version: number | null;
};

async function fetchArtifact(): Promise<PeArtifact> {
  const token = await getJWTToken();
  const response = await fetch(`${apiUrl}/program-evaluation`, {
    headers: authHeaders(token),
  });
  if (response.status === 404) {
    return { report: null, content: null, stale: false, generatedAt: null, version: null };
  }
  if (!response.ok) {
    throw new Error(`program-evaluation fetch failed: ${response.status}`);
  }
  const data = (await response.json()) as PeArtifactResponse;
  const report = data.report
    ? { ...data.report, drilldown: await breakdownsFor(data.report.district?.school_year, data.version) }
    : null;
  return {
    report,
    content: data.content
      ? { ...data.content, unit_mode: data.content.unit_mode ?? data.unitMode }
      : null,
    stale: data.stale === true,
    generatedAt: typeof data.generatedAt === 'string' && data.generatedAt !== '' ? data.generatedAt : null,
    version: typeof data.version === 'number' ? data.version : null,
  };
}

async function breakdownsFor(schoolYear: string | undefined, version: number | undefined): Promise<PeDrilldown | null | undefined> {
  try {
    const { drilldown, schoolsOmitted } = await fetchDrilldown({ schoolYear, version: typeof version === 'number' ? version : undefined });
    return schoolsOmitted ? { ...drilldown, schools_omitted: schoolsOmitted } : drilldown;
  } catch (err: unknown) {
    return err instanceof DrilldownNotFound ? null : undefined;
  }
}

let cachedArtifact: Promise<PeArtifact> | null = null;

export function loadProgramEvaluation(): Promise<PeArtifact> {
  if (cachedArtifact === null) {
    const pending = fetchArtifact()
      .then((artifact) => {
        if (artifact.report && artifact.report.drilldown === undefined && cachedArtifact === pending) cachedArtifact = null;
        return artifact;
      })
      .catch((err: unknown) => {
        if (cachedArtifact === pending) cachedArtifact = null;
        throw err;
      });
    cachedArtifact = pending;
  }
  return cachedArtifact;
}

export function invalidateProgramEvaluation(): void {
  cachedArtifact = null;
}

const defaultSource: PeDataSource = () => loadProgramEvaluation();

export function useProgramEvaluationReport(): {
  report: PeReport | null;
  content: PeContent | null;
  generatedAt: string | null;
  version: number | null;
  loading: boolean;
  error: Error | null;
} {
  const [artifact, setArtifact] = useState<PeArtifact | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let active = true;
    loadProgramEvaluation()
      .then((next) => {
        if (active) setArtifact(next);
      })
      .catch((err: unknown) => {
        if (active) setError(err instanceof Error ? err : new Error(String(err)));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  return {
    report: artifact?.report ?? null,
    content: artifact?.content ?? null,
    generatedAt: artifact?.generatedAt ?? null,
    version: artifact?.version ?? null,
    loading,
    error,
  };
}

export async function hasCurrentYearReport(signal?: AbortSignal): Promise<boolean> {
  try {
    const token = await getJWTToken();
    const response = await fetch(`${apiUrl}/program-evaluation`, {
      headers: authHeaders(token),
      signal,
    });
    return response.ok;
  } catch {
    return false;
  }
}

export function useProgramEvaluation(source: PeDataSource = defaultSource): {
  report: PeReport | null;
  content: PeContent | null;
  stale: boolean;
  generatedAt: string | null;
  loading: boolean;
  error: Error | null;
  refetch: () => void;
} {
  const [report, setReport] = useState<PeReport | null>(null);
  const [content, setContent] = useState<PeContent | null>(null);
  const [stale, setStale] = useState(false);
  const [generatedAt, setGeneratedAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [fetchCount, setFetchCount] = useState(0);

  const sourceRef = useRef(source);

  useEffect(() => {
    let active = true;

    sourceRef
      .current()
      .then((artifact) => {
        if (!active) return;
        setReport(artifact.report);
        setContent(artifact.content);
        setStale(artifact.stale === true);
        setGeneratedAt(artifact.generatedAt ?? null);
      })
      .catch((err: unknown) => {
        if (!active) return;
        setError(err instanceof Error ? err : new Error(String(err)));
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [fetchCount]);

  const refetch = useCallback(() => {
    invalidateProgramEvaluation();
    setLoading(true);
    setError(null);
    setFetchCount((count) => count + 1);
  }, []);

  return { report, content, stale, generatedAt, loading, error, refetch };
}
